/**
 * The HTTP client every other module talks to the server through.
 *
 * It owns four things nothing else should have to think about: the base path,
 * CSRF, the bearer fallback, and the single-flight access token refresh.
 */

import type {
  ApiErrorCode,
  ApiErrorDetails,
  FieldErrors,
  RefreshRequest,
} from '@/api/types'
import { recordDiagnostic } from '@/lib/diagnostics'

const DEFAULT_BASE_PATH = '/api/client/v1'
const CSRF_COOKIE = 'relaii_csrf'
const CSRF_HEADER = 'X-Relaii-CSRF'
const REFRESH_PATH = '/auth/refresh'
const REFRESH_LOCK_PREFIX = 'relaii:session-refresh:'
const ABSOLUTE_URL = /^https?:\/\//i
const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS'])
export const REQUEST_TIMEOUT_MS = 30_000
export const UPLOAD_TIMEOUT_MS = 15 * 60_000
let sessionGeneration = 0
let sessionRequests = new AbortController()

/**
 * Dispatched on `window` when the refresh token is no longer accepted. The
 * session is gone at that point and the only correct response is to return to
 * the login screen; the client cannot recover on its own.
 */
export const SESSION_EXPIRED_EVENT = 'relaii:session-expired'

/**
 * Dispatched when the server requires two-factor enrolment before the rest of
 * the API will answer. The session is still valid; the caller is sent to set
 * up an authenticator rather than back to sign-in.
 */
export const MFA_ENROLLMENT_EVENT = 'relaii:mfa-enrollment-required'

export type QueryParams = Record<string, string | number | boolean | undefined>

// ── Errors ──────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly details: ApiErrorDetails

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    details: ApiErrorDetails = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /**
   * `details` for a `validation_failed` response, as field → messages.
   * Anything in `details` that is not a string or an array of strings is
   * dropped, so a form can bind to this without further narrowing.
   */
  get fieldErrors(): FieldErrors {
    const errors: FieldErrors = {}

    for (const [field, value] of Object.entries(this.details)) {
      if (typeof value === 'string') {
        errors[field] = [value]
      } else if (Array.isArray(value)) {
        const messages = value.filter((item): item is string => typeof item === 'string')
        if (messages.length > 0) errors[field] = messages
      }
    }

    return errors
  }

  /** `details.retry_after_seconds` for a 429, otherwise null. */
  get retryAfterSeconds(): number | null {
    const value = this.details['retry_after_seconds']
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

// ── Configuration ───────────────────────────────────────────────────────────

let basePath = DEFAULT_BASE_PATH

/**
 * The access token, held in a module-scoped variable and nowhere else.
 *
 * Never localStorage or sessionStorage: a token any script on the page can
 * read is a token an XSS payload can exfiltrate and replay from anywhere,
 * which is the entire reason the web client authenticates with HttpOnly
 * cookies instead. This slot exists for native clients, which keep the token
 * in the OS keychain and hand it over once per launch; on a page reload it is
 * simply gone, and that is correct.
 */
let accessToken: string | null = null

/** A refresh in progress, shared by every request that hit a 401 at once. */
let refreshInFlight: Promise<void> | null = null
let refreshRevision = ''

// This is only a completion marker, never a credential. Tabs share cookies,
// so a delayed 401 must reuse a rotation completed after its request started.
function currentRefreshRevision(): string {
  if (accessToken !== null) return refreshRevision
  try { return localStorage.getItem(`${REFRESH_LOCK_PREFIX}${basePath}:revision`) ?? refreshRevision }
  catch { return refreshRevision }
}

function recordRefresh(): void {
  refreshRevision = crypto.randomUUID()
  if (accessToken === null) {
    try { localStorage.setItem(`${REFRESH_LOCK_PREFIX}${basePath}:revision`, refreshRevision) }
    catch { /* Restricted storage still has the per-tab completion marker. */ }
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Serialises params, dropping any that are undefined. */
export function buildQuery(params?: QueryParams): string {
  if (!params) return ''

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    search.append(key, String(value))
  }

  const query = search.toString()
  return query === '' ? '' : `?${query}`
}

function buildUrl(path: string, params?: QueryParams): string {
  const target = ABSOLUTE_URL.test(path)
    ? path
    : `${basePath}${path.startsWith('/') ? path : `/${path}`}`

  return `${target}${buildQuery(params)}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null

  const prefix = `${name}=`
  for (const part of document.cookie.split(';')) {
    const entry = part.trim()
    if (!entry.startsWith(prefix)) continue

    const raw = entry.slice(prefix.length)
    try {
      return decodeURIComponent(raw)
    } catch {
      // A cookie value that is not valid percent-encoding is not ours to fix;
      // pass it through rather than failing every unsafe request.
      return raw
    }
  }

  return null
}

function fallbackCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'invalid_request'
    case 401:
      return 'unauthenticated'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 409:
      return 'conflict'
    case 413:
      return 'payload_too_large'
    case 422:
      return 'validation_failed'
    case 429:
      return 'rate_limited'
    case 503:
      return 'not_ready'
    default:
      return 'invalid_request'
  }
}

/**
 * Turns a failed response into an ApiError, consuming its body.
 *
 * A failure can also come from a proxy or a crash page rather than from relaii,
 * so an unparseable body falls back to a code derived from the status instead
 * of throwing a second error on top of the first.
 */
async function toApiError(response: Response): Promise<ApiError> {
  let code = fallbackCode(response.status)
  let message = response.statusText || `The request failed with status ${response.status}.`
  let details: ApiErrorDetails = {}

  let raw = ''
  try {
    raw = await response.text()
  } catch {
    raw = ''
  }

  if (raw !== '') {
    try {
      const parsed: unknown = JSON.parse(raw)
      const envelope: unknown = isRecord(parsed) ? parsed['error'] : null

      if (isRecord(envelope)) {
        const envelopeCode = envelope['code']
        const envelopeMessage = envelope['message']
        const envelopeDetails = envelope['details']

        // A newer server may use a code this build has never heard of. Widening
        // it here keeps the value intact for logging and for a default branch,
        // rather than discarding it to satisfy the union.
        if (typeof envelopeCode === 'string') code = envelopeCode as ApiErrorCode
        if (typeof envelopeMessage === 'string') message = envelopeMessage
        if (isRecord(envelopeDetails)) details = envelopeDetails
      }
    } catch {
      // Not our envelope; the status-derived code stands.
    }
  }

  return new ApiError(response.status, code, message, details)
}

async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as unknown as T

  const raw = await response.text()
  if (raw === '') return undefined as unknown as T

  return JSON.parse(raw) as T
}

// ── The request pipeline ────────────────────────────────────────────────────

interface RequestOptions {
  method: string
  path: string
  body?: unknown
  params?: QueryParams
  signal?: AbortSignal
  responseType?: 'blob'
}

/**
 * Issues one request. Deliberately does no retrying, so the refresh path can
 * use it without recursing back into itself.
 */
function send(options: RequestOptions): Promise<Response> {
  const headers = new Headers({ Accept: options.responseType === 'blob' ? 'image/*' : 'application/json' })
  const init: RequestInit = {
    method: options.method,
    headers,
    // Cookies are the primary credential and the API is same-origin, served by
    // the same Phoenix endpoint that serves this bundle.
    credentials: 'same-origin',
  }

  if (options.signal) init.signal = options.signal

  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    init.body = JSON.stringify(options.body)
  }

  if (accessToken !== null) {
    // A bearer token is never attached by the browser on its own, so there is
    // nothing for CSRF to protect against and the server does not check it.
    headers.set('Authorization', `Bearer ${accessToken}`)
  } else if (!SAFE_METHODS.has(options.method)) {
    // Cookie auth: the browser would attach the session cookie to a request
    // any other origin made, so unsafe methods carry a token only same-origin
    // script can read back out of the cookie jar.
    const csrf = readCookie(CSRF_COOKIE)
    if (csrf !== null) headers.set(CSRF_HEADER, csrf)
  }

  return fetch(buildUrl(options.path, options.params), init)
}

/**
 * Serialises cookie refreshes across every tab on this origin.
 *
 * `refreshInFlight` protects one JavaScript context, but each browser tab has
 * its own module instance. Without an origin-wide lock, two tabs waking up to
 * an expired access cookie can spend the same rotating refresh token at once.
 * The server correctly treats the losing request as token reuse and revokes
 * the session, which looks to the person using the app like a random logout.
 *
 * Bearer clients keep independent credentials and must not share this lock.
 * Browsers without the Web Locks API retain the per-tab single-flight guard.
 */
function withBrowserRefreshLock(work: () => Promise<void>): Promise<void> {
  if (
    accessToken !== null ||
    typeof navigator === 'undefined' ||
    navigator.locks === undefined
  ) {
    return work()
  }

  // TypeScript's DOM declaration models the callback's promise as the value
  // inside the returned promise. Native promises adopt that inner promise at
  // runtime; the explicit `then` expresses the same flattening to the type
  // checker.
  return navigator.locks
    .request(`${REFRESH_LOCK_PREFIX}${basePath}`, { mode: 'exclusive', signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) }, work)
    .then((pending) => pending)
}

/** Only an authentication refusal proves that the session itself is gone. */
function refreshFailureEndsSession(error: unknown): boolean {
  return isApiError(error) && (error.status === 401 || error.code === 'account_disabled')
}

/**
 * Refreshes the access token, at most once per tab and one tab at a time.
 *
 * Refresh tokens rotate on every use: the response carries a new one and the
 * presented one stops working immediately. An access token expiring while
 * several requests are in flight therefore produces a burst of simultaneous
 * 401s, and if each fired its own refresh the server would rotate N times.
 * Only the last rotation survives, so every other caller — and the cookie the
 * browser now holds — would be left presenting a token the server has already
 * retired, logging the user out for no reason. Sharing one promise makes the
 * whole burst cost exactly one rotation, and the losers simply await it.
 */
function refreshSession(failedRevision: string): Promise<void> {
  if (refreshInFlight !== null) return refreshInFlight
  const generation = sessionGeneration

  const attempt = async (): Promise<void> => {
    if (generation !== sessionGeneration) throw new DOMException('Session changed', 'AbortError')
    if (currentRefreshRevision() !== failedRevision) return
    // The refresh token travels in the HttpOnly cookie for the web client; a
    // bearer host that holds one of its own passes it through this body.
    const body: RefreshRequest = {}
    const parsed = await request<unknown>({ method: 'POST', path: REFRESH_PATH, body }, false)
    const rotated: unknown = isRecord(parsed) ? parsed['access_token'] : undefined

    // Only meaningful in bearer mode. In cookie mode the new tokens arrive as
    // Set-Cookie headers and adopting one here would silently switch this
    // client to bearer auth, and with it stop sending the CSRF header.
    if (accessToken !== null && typeof rotated === 'string') {
      accessToken = rotated
    }
    recordRefresh()
  }

  const pending = withBrowserRefreshLock(attempt)
    .catch((error: unknown) => {
      // A network interruption, proxy error or server restart does not revoke
      // the refresh token. Preserve the session so the next request can try
      // again instead of turning a temporary outage into a forced login.
      if (generation === sessionGeneration && refreshFailureEndsSession(error)) {
        accessToken = null
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
        }
      }
      throw error
    })
    .finally(() => {
      if (refreshInFlight === pending) refreshInFlight = null
    })

  refreshInFlight = pending
  return pending
}

async function request<T>(options: RequestOptions, allowRefresh = true): Promise<T> {
  const generation = sessionGeneration
  const controller = new AbortController()
  const sources = [options.signal, sessionRequests.signal].filter((signal): signal is AbortSignal => signal !== undefined)
  const abort = (): void => controller.abort()
  for (const signal of sources) {
    if (signal.aborted) controller.abort()
    else signal.addEventListener('abort', abort, { once: true })
  }
  const timeout = setTimeout(() => {
    recordDiagnostic('request_timeout')
    controller.abort(new DOMException('The request timed out. Try again.', 'TimeoutError'))
  }, REQUEST_TIMEOUT_MS)
  try {
    const result = await performRequest<T>({ ...options, signal: controller.signal }, allowRefresh)
    if (generation !== sessionGeneration || controller.signal.aborted) throw controller.signal.reason ?? abortError()
    return result
  } catch (error) {
    if (error instanceof TypeError && !controller.signal.aborted) recordDiagnostic('request_network_error')
    throw error
  } finally {
    clearTimeout(timeout)
    for (const signal of sources) signal.removeEventListener('abort', abort)
  }
}

async function performRequest<T>(options: RequestOptions, allowRefresh: boolean): Promise<T> {
  const failedRevision = currentRefreshRevision()
  const response = await send(options)
  if (options.signal?.aborted) throw options.signal.reason ?? abortError()

  if (response.status === 401 && allowRefresh) {
    const error = await toApiError(response)
    if (options.signal?.aborted) throw options.signal.reason ?? abortError()

    if (error.code === 'token_expired') {
      // Rejects if the session is truly gone, which is what the caller wants:
      // the original request can never succeed from here.
      await refreshSession(failedRevision)
      // Replayed from the original options rather than from the Response, so
      // the body is serialised afresh instead of being read twice.
      return request<T>(options, false)
    }

    throw error
  }

  if (!response.ok) {
    const error = await toApiError(response)
    if (options.signal?.aborted) throw options.signal.reason ?? abortError()

    if (error.code === 'mfa_enrollment_required' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(MFA_ENROLLMENT_EVENT))
    }

    throw error
  }

  if (options.responseType === 'blob') return await response.blob() as T
  return parseBody<T>(response)
}

// ── Uploads ─────────────────────────────────────────────────────────────────

function abortError(): DOMException {
  return new DOMException('The upload was aborted.', 'AbortError')
}

/**
 * Uploads the bytes of a file to a presigned URL, reporting progress 0..1.
 *
 * XMLHttpRequest rather than fetch, because upload progress has no fetch
 * equivalent that is usable across the browsers this client supports.
 */
function upload(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }

    const xhr = new XMLHttpRequest()
    // The contract fixes step 2 of the upload flow at PUT for both backends.
    xhr.open('PUT', url, true)
    xhr.timeout = UPLOAD_TIMEOUT_MS

    // The presigned URL is the credential, and it may point at a third-party
    // bucket. Sending relaii's cookies or CSRF token there would hand session
    // material to a host with no business seeing it, and would break an S3
    // signature that covers the headers.
    xhr.withCredentials = false

    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value)
    }

    const onAbort = (): void => xhr.abort()
    if (signal) signal.addEventListener('abort', onAbort, { once: true })

    const cleanup = (): void => {
      if (signal) signal.removeEventListener('abort', onAbort)
    }

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(Math.min(event.loaded / event.total, 1))
        }
      })
    }

    xhr.addEventListener('load', () => {
      cleanup()
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(1)
        resolve()
      } else {
        reject(
          new ApiError(
            xhr.status,
            fallbackCode(xhr.status),
            `The file could not be stored (status ${xhr.status}).`,
          ),
        )
      }
    })

    xhr.addEventListener('error', () => {
      cleanup()
      reject(new Error('The upload could not reach the storage backend.'))
    })

    xhr.addEventListener('timeout', () => {
      cleanup()
      reject(new Error('The upload timed out.'))
    })

    xhr.addEventListener('abort', () => {
      cleanup()
      reject(abortError())
    })

    xhr.send(file)
  })
}

// ── Public surface ──────────────────────────────────────────────────────────

export const api = {
  image(path: string, signal?: AbortSignal): Promise<Blob> {
    return request<Blob>({ method: 'GET', path, signal, responseType: 'blob' })
  },
  /** End all requests made under the previous browser identity. */
  invalidateSession(): void {
    sessionGeneration += 1
    sessionRequests.abort()
    sessionRequests = new AbortController()
    refreshInFlight = null
  },
  get<T>(path: string, params?: QueryParams, signal?: AbortSignal): Promise<T> {
    return request<T>({ method: 'GET', path, params, signal })
  },

  post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return request<T>({ method: 'POST', path, body, signal })
  },

  patch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return request<T>({ method: 'PATCH', path, body, signal })
  },

  del<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return request<T>({ method: 'DELETE', path, body, signal })
  },

  upload,

  /**
   * Sets the bearer token used instead of cookies, or clears it with null.
   * Held in memory only — see the note on the variable itself.
   */
  setAccessToken(token: string | null): void {
    accessToken = token
  },

  /** Whether a bearer token is currently in use rather than cookies. */
  hasAccessToken(): boolean {
    return accessToken !== null
  },

  /** Overrides the API base path. Defaults to `/api/client/v1`. */
  setBasePath(path: string): void {
    basePath = path.replace(/\/+$/, '')
  },

  getBasePath(): string {
    return basePath
  },
}
